import { queryOptions, useQuery } from '@tanstack/react-query';
import type { ScmProviderId } from '@/types/scm-provider';
import { inferScmProviderFromRepoUrl } from '@/types/scm-provider';
import { scmPost } from './scmClient';

export type FileContentCoverageBucket = 'combined' | 'manual' | 'automation';

export interface FileContentRequest {
  repoUrl: string;
  branchName: string;
  filePath: string;
  scanId: string;
  pat?: string;
  provider?: ScmProviderId;
  /** Which scanFiles coverage bucket to return (default combined). */
  coverageBucket?: FileContentCoverageBucket;
}

interface LineClassification {
  executable: number[];
  nonExecutable: number[];
  import: number[];
  blank: number[];
  comment: number[];
  decorator: number[];
  typeDeclaration: number[];
}

interface Coverage {
  linesOfCodeMode: {
    coveredLines: number[];
  };
}

export interface FileContentData {
  content: string;
  language: string;
  lineClassification: LineClassification;
  coverage: Coverage;
}

interface FileContentResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: FileContentData;
}

export const getFileContent = (
  req: FileContentRequest,
): Promise<FileContentResponse> => {
  const provider = req.provider ?? inferScmProviderFromRepoUrl(req.repoUrl);
  const pat = req.pat ?? '';
  const coverageBucket = req.coverageBucket ?? 'combined';

  return scmPost<FileContentResponse>(
    provider,
    '/api/scm/fetch-file-content',
    pat,
    {
      repoUrl: req.repoUrl,
      branchName: req.branchName,
      filePath: req.filePath,
      scanId: req.scanId,
      coverageBucket,
    },
  );
};

export const getFileContentQueryOptions = (req: FileContentRequest) => {
  const provider = req.provider ?? inferScmProviderFromRepoUrl(req.repoUrl);
  const coverageBucket = req.coverageBucket ?? 'combined';

  return queryOptions({
    queryKey: [
      'file-content',
      provider,
      req.scanId,
      req.filePath,
      coverageBucket,
    ],
    queryFn: () => getFileContent(req),
    enabled: !!req.filePath && !!req.scanId,
  });
};

export const useFileContent = (req: FileContentRequest) =>
  useQuery(getFileContentQueryOptions(req));
